import React, { useEffect, useRef } from "react";
import { bestMonth, cellStyle, metricValue, fmtMetric, fmtMonth, fmtPct, fmtAED, momPct, N } from "./intel";

function MonthChips({ months, sel, onSelect, partial, isAR, t }) {
  const ref = useRef(null);
  useEffect(() => {
    const box = ref.current;
    const el = box?.querySelector('[aria-pressed="true"]');
    if (el) {
      const target = el.offsetLeft - (box.clientWidth - el.offsetWidth) / 2;
      box.scrollLeft = isAR ? target - (box.scrollWidth - box.clientWidth) : target;
    }
  }, [sel.month, isAR]);
  return (
    <div ref={ref} className="-mx-3.5 px-3.5 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="mobile-month-chips">
      {months.map((m) => (
        <button key={m} type="button" aria-pressed={m === sel.month} onClick={() => onSelect({ month: m })}
          className={`shrink-0 h-9 px-4 rounded-full text-[12px] font-semibold border transition-colors ${m === sel.month ? "bg-propertic-navy text-propertic-paper border-propertic-navy" : "bg-white border-propertic-hairline-2 text-propertic-ink-2"}`}
          data-testid={`mobile-month-${m}`}>{fmtMonth(m, isAR, true)}{m === partial && <span className="ms-1.5 text-[10px] font-normal opacity-80">· {t.inProgress}</span>}</button>
      ))}
    </div>
  );
}

function Spark({ r, months, month, metric, best, partial }) {
  const vals = months.map((m) => (metric === "value" ? r.values[m] : r.counts[m]));
  const max = Math.max(...vals, 1);
  const cls = (m) => (m === partial ? "bg-white border border-dashed border-propertic-hairline-2" : m === best ? "bg-propertic-navy" : m === month ? "bg-propertic-gold" : "bg-propertic-sand");
  return (
    <div className="flex items-end gap-1 h-8 w-full max-w-[240px]" aria-hidden="true">
      {vals.map((v, i) => (
        <span key={months[i]} className={`block flex-1 rounded-t-[3px] ${cls(months[i])}`} style={{ height: `${Math.max(6, (v / max) * 100)}%` }} />
      ))}
    </div>
  );
}

export default function MobileActivity({ rows, months, allMonths, metric, sel, onSelect, onOpen, partial, isAR, t }) {
  const isPartial = sel.month === partial;
  return (
    <div data-testid="mobile-activity">
      <MonthChips months={months} sel={sel} onSelect={onSelect} partial={partial} isAR={isAR} t={t} />
      <p className="mt-3 text-[11px] text-propertic-muted">{t.tapHint}</p>
      <ol className="mt-2 space-y-2" data-testid="mobile-area-list">
        {rows.map((r, idx) => {
          const v = metricValue(metric, r, allMonths, sel.month);
          const mom = isPartial ? null : momPct(r, allMonths, sel.month);
          const best = bestMonth(metric, r, months, allMonths, partial);
          const st = cellStyle(v, sel.month === best, isPartial);
          return (
            <li key={r.key}>
              <button type="button" onClick={() => { onSelect({ key: r.key }); onOpen(); }}
                className="w-full rounded-2xl border hairline bg-white px-3.5 py-3 text-start active:scale-[0.99] transition-transform"
                data-testid={`mobile-area-${r.slug}`}>
                <span className="flex items-start gap-2.5">
                  <span className="w-5 pt-0.5 text-[11px] text-propertic-muted tabular-nums">{N(idx + 1, isAR)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-editorial text-[15px] leading-snug text-propertic-navy">{r.label}</span>
                    <span className="block text-[11px] text-propertic-muted mt-0.5 tabular-nums">
                      {metric === "value" ? `${N(r.counts[sel.month], isAR)} ${isAR ? "صفقة" : "sales"}` : fmtAED(r.values[sel.month], isAR)}
                      {metric !== "change" && mom != null && <span className={mom >= 0 ? "text-propertic-navy-2" : "text-propertic-rust"}> · {fmtPct(mom, isAR)}</span>}
                    </span>
                  </span>
                  <span className="shrink-0 min-w-[68px] text-center rounded-lg px-2 py-1.5 text-[13px] font-semibold tabular-nums" style={st} data-testid={`mobile-area-${r.slug}-value`}>
                    {fmtMetric(metric, v, isAR)}
                  </span>
                </span>
                <span className="mt-2.5 flex ps-7"><Spark r={r} months={metric === "change" ? allMonths : months} month={sel.month} metric={metric} best={best} partial={partial} /></span>
              </button>
            </li>
          );
        })}
      </ol>
      {!rows.length && <p className="py-6 text-sm text-propertic-muted" data-testid="matrix-no-match">{t.noMatch}</p>}
    </div>
  );
}
